import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  return <AnimatePresence data-id="element-44">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-45">
        <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} transition={{
        duration: 0.3
      }} className="bg-night-800 border border-night-700 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-glow-lg" onClick={e => e.stopPropagation()} data-id="element-46">
          <div className="sticky top-0 bg-night-800 border-b border-night-700 px-6 py-4 flex items-center justify-between z-10" data-id="element-47">
            <h2 className="font-serif text-2xl text-white" data-id="element-48">
              {type === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
            </h2>
            <button onClick={onClose} className="text-cream/50 hover:text-white transition-colors p-1" aria-label="Close" data-id="element-49">
              <XIcon className="w-5 h-5" data-id="element-50" />
            </button>
          </div>

          <div className="px-6 py-6 text-cream/80 leading-relaxed space-y-6 text-sm" data-id="element-51">
            {type === 'privacy' ? <PrivacyContent data-id="element-52" /> : <TermsContent data-id="element-53" />}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
function PrivacyContent() {
  return <>
      <p className="text-cream/50 text-xs" data-id="element-54">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}
      </p>

      <section data-id="element-55">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-56">
          1. Information We Collect
        </h3>
        <p data-id="element-57">
          When you purchase The 3:15 AM Panic Switch protocol, we collect your
          name, email address, and payment information necessary to process your
          order and deliver the digital product. We do not store credit card
          details on our servers—payment processing is handled by secure
          third-party providers.
        </p>
      </section>

      <section data-id="element-58">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-59">
          2. How We Use Your Information
        </h3>
        <p data-id="element-60">
          We use the information collected to deliver your purchased product,
          send order confirmations, provide customer support, and send
          occasional product-related updates. You may opt out of marketing
          communications at any time.
        </p>
      </section>

      <section data-id="element-61">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-62">
          3. Data Protection
        </h3>
        <p data-id="element-63">
          We implement industry-standard security measures to protect your
          personal information. Your data is encrypted in transit and at rest.
          We do not sell, trade, or rent your personal information to third
          parties.
        </p>
      </section>

      <section data-id="element-64">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-65">4. Cookies</h3>
        <p data-id="element-66">
          Our website uses essential cookies to ensure proper functionality and
          analytics cookies to understand how visitors interact with our site.
          You can disable cookies through your browser settings.
        </p>
      </section>

      <section data-id="element-67">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-68">5. Your Rights</h3>
        <p data-id="element-69">
          You have the right to access, correct, or delete your personal data at
          any time. To exercise these rights, please contact us at the email
          address provided below.
        </p>
      </section>

      <section data-id="element-70">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-71">6. Contact</h3>
        <p data-id="element-72">
          If you have any questions about this Privacy Policy, please contact us
          at support@315ampanic.com.
        </p>
      </section>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-cream/50 text-xs" data-id="element-73">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}
      </p>

      <section data-id="element-74">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-75">
          1. Product Description
        </h3>
        <p data-id="element-76">
          The 3:15 AM Panic Switch is a digital informational protocol delivered
          electronically upon purchase. It is designed for educational purposes
          and does not constitute medical advice, diagnosis, or treatment.
        </p>
      </section>

      <section data-id="element-77">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-78">
          2. Medical Disclaimer
        </h3>
        <p data-id="element-79">
          The content provided in this protocol is for informational purposes
          only. Always consult with a qualified healthcare professional before
          making changes to your health routine. Individual results may vary.
          This product is not intended to replace professional medical advice.
        </p>
      </section>

      <section data-id="element-80">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-81">
          3. Digital Delivery
        </h3>
        <p data-id="element-82">
          Upon successful payment, you will receive immediate access to the
          digital protocol. Due to the digital nature of this product, all sales
          are final. If you experience any issues with delivery, please contact
          our support team.
        </p>
      </section>

      <section data-id="element-83">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-84">
          4. Intellectual Property
        </h3>
        <p data-id="element-85">
          All content within The 3:15 AM Panic Switch protocol is protected by
          copyright. You may not reproduce, distribute, or share the content
          without written permission. Your purchase grants you a personal,
          non-transferable license to use the material.
        </p>
      </section>

      <section data-id="element-86">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-87">
          5. Limitation of Liability
        </h3>
        <p data-id="element-88">
          We are not liable for any direct, indirect, or consequential damages
          arising from the use of this protocol. Use of the information provided
          is at your own risk and discretion.
        </p>
      </section>

      <section data-id="element-89">
        <h3 className="text-white font-bold text-base mb-2" data-id="element-90">
          6. Changes to Terms
        </h3>
        <p data-id="element-91">
          We reserve the right to update these terms at any time. Continued use
          of the product after changes constitutes acceptance of the updated
          terms.
        </p>
      </section>
    </>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 px-4 border-t border-night-800" data-id="element-92">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-93">
          <p className="text-cream/40 text-sm" data-id="element-94">
            © {new Date().getFullYear()} The 3:15 AM Panic Switch. All rights
            reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-95">
            <button onClick={() => setActiveModal('privacy')} className="text-cream/40 hover:text-amber-500 text-sm transition-colors" data-id="element-96">
              Privacy Policy
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-cream/40 hover:text-amber-500 text-sm transition-colors" data-id="element-97">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      <Modal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-98" />
    </>;
}